import { Directive, OnDestroy, OnInit, signal } from '@angular/core';
import { MatPaginatorIntl, PageEvent } from '@angular/material/paginator';
import { Observable, Subject, Subscription, debounceTime, finalize } from 'rxjs';
import { createFrenchPaginatorIntl } from './atlas-list-page.paginator';

export { createFrenchPaginatorIntl };

type AtlasViewerSearchPageEnvelope = {
  pageIndex: number;
  pageSize: number;
};

@Directive()
export abstract class AtlasViewerSearchPageBase<TPage extends AtlasViewerSearchPageEnvelope>
  implements OnInit, OnDestroy {
  readonly pageSizeOptions = [10, 25, 50, 100];
  readonly loading = signal(false);
  readonly searchPending = signal(false);
  readonly error = signal<string | undefined>(undefined);
  readonly page = signal<TPage | undefined>(undefined);
  readonly pageIndex = signal(0);
  readonly pageSize = signal(25);

  private readonly filterChanges = new Subject<void>();
  private loadSubscription?: Subscription;
  private filterSubscription?: Subscription;
  private loadRequestId = 0;

  ngOnInit(): void {
    this.filterSubscription = this.filterChanges.pipe(debounceTime(300)).subscribe(() => {
      this.pageIndex.set(0);
      this.loadPage();
    });
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.filterSubscription?.unsubscribe();
  }

  onPageChange(event: PageEvent): void {
    this.pageIndex.set(event.pageIndex);
    this.pageSize.set(event.pageSize);
    this.loadPage();
  }

  reload(): void {
    this.loadPage();
  }

  protected loadPage(): void {
    const requestId = ++this.loadRequestId;
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = undefined;
    this.searchPending.set(false);

    if (!this.hasActiveFilters()) {
      this.page.set(undefined);
      this.error.set(undefined);
      this.loading.set(false);
      return;
    }

    this.loading.set(true);
    this.error.set(undefined);
    this.page.set(undefined);

    this.loadSubscription = this.loadPageRequest(this.pageIndex(), this.pageSize())
      .pipe(
        finalize(() => {
          if (requestId === this.loadRequestId) {
            this.loading.set(false);
          }
        })
      )
      .subscribe({
        next: page => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.page.set(page);
          this.pageIndex.set(page.pageIndex);
          this.pageSize.set(page.pageSize);
        },
        error: error => {
          if (requestId !== this.loadRequestId) {
            return;
          }

          this.page.set(undefined);
          this.error.set(this.getLoadErrorMessage(error));
        },
      });
  }

  protected queueFilterReload(): void {
    if (!this.hasActiveFilters()) {
      this.loadPage();
      return;
    }

    this.searchPending.set(true);
    this.filterChanges.next();
  }

  protected parseOptionalNumber(value: string): number | null {
    const trimmed = value.trim();

    if (!trimmed) {
      return null;
    }

    const parsedValue = Number(trimmed);

    return Number.isInteger(parsedValue) && parsedValue >= 0 ? parsedValue : null;
  }

  abstract hasActiveFilters(): boolean;
  protected abstract loadPageRequest(pageIndex: number, pageSize: number): Observable<TPage>;
  protected abstract getLoadErrorMessage(error: unknown): string;
}

export const atlasViewerSearchPaginatorProvider = {
  provide: MatPaginatorIntl,
  useFactory: createFrenchPaginatorIntl,
};
